import React from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Button,
  Divider,
  Paper,
  Stack,
  IconButton,
  Breadcrumbs,
  Link,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  ArrowBack,
  LocalFireDepartment,
  Security,
  CheckCircle,
  Engineering,
  ViewInAr,
  Download,
  Share,
  Favorite,
  FavoriteBorder,
  ZoomIn,
  NavigateNext,
  Shield,
  VerifiedUser,
  Description,
  LocalShipping,
  Support,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ProductDetail: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // File-based product detail
  const [fetchedProduct, setFetchedProduct] = React.useState<any | null>(null);
  React.useEffect(() => {
    if (!slug) return;
    fetch(`/data/products/${slug}/index.json`, { cache: "no-store" })
      .then((r) => r.json())
      .then((data) => setFetchedProduct(data))
      .catch(() => setFetchedProduct(null));
  }, [slug]);
  const [activeImage, setActiveImage] = React.useState(0);
  const [tabValue, setTabValue] = React.useState(0);
  const [liked, setLiked] = React.useState(false);

  const langIsTr = i18n.language.startsWith("tr");

  // Merge fetched product with minimal safe defaults for rendering
  const product = fetchedProduct || {
    id: 1,
    title: "EI90 Çift Kanatlı Yangın Kapısı",
    subtitle: "Profesyonel Yangın Güvenlik Çözümü",
    fireRating: "EI90",
    category: "Çift Kanat",
    price: 4200,
    oldPrice: 5500,
    discount: 24,
    sku: "YK-EI90-CK-2024",
    stock: "Stokta Var",
    images: [
      `/uploads/AREL.png`,
      `/uploads/ALEV.png`,
      `/uploads/ALEV CAMLI.png`,
      `/uploads/AYLA 2.png`,
    ],
    features: [
      { icon: <LocalFireDepartment />, text: "90 dakika yangın dayanımı" },
      { icon: <Security />, text: "TSE ve CE sertifikalı" },
      { icon: <Engineering />, text: "Otomatik kapanma sistemi" },
      { icon: <Shield />, text: "Duman sızdırmazlık özelliği" },
    ],
    specifications: {
      general: [
        { label: "Model Kodu", value: "YK-EI90-CK" },
        { label: "Yangın Dayanımı", value: "EI90 (90 dakika)" },
        { label: "Kanat Tipi", value: "Çift Kanat" },
        { label: "Üretim Yılı", value: "2024" },
      ],
      physical: [
        { label: "Boyutlar", value: "2100 x 1400 x 54 mm" },
        { label: "Ağırlık", value: "85 kg" },
        { label: "Kalınlık", value: "54 mm" },
        { label: "Malzeme", value: "Galvanizli çelik sac" },
      ],
      technical: [
        { label: "Kilit Sistemi", value: "Panik bar + Silindir kilit" },
        { label: "Menteşe", value: "Yangın kapısı menteşesi (3 adet)" },
        { label: "Ses Yalıtımı", value: "Rw = 32 dB" },
        { label: "Isı Yalıtımı", value: "U = 2.1 W/m²K" },
      ],
      certificates: [
        { label: "TSE Belgesi", value: "TSE 441" },
        { label: "CE Belgesi", value: "EN 16034" },
        { label: "Test Standardı", value: "TS EN 1634-1" },
        { label: "Kalite Belgesi", value: "ISO 9001:2015" },
      ],
    },
    colors: [
      { name: "RAL 9016", hex: "#F7F8F2", label: "Beyaz" },
      { name: "RAL 7035", hex: "#C5C7C4", label: "Açık Gri" },
      { name: "RAL 3000", hex: "#A72920", label: "Kırmızı" },
      { name: "RAL 9005", hex: "#0A0A0D", label: "Siyah" },
    ],
    description: `Profesyonel yangın güvenliği için tasarlanmış EI90 sınıfı çift kanatlı yangın kapısı, 
    90 dakika yangın dayanımı ile can ve mal güvenliğinizi en üst düzeyde korur. TSE ve CE belgeli 
    üretimimiz, yüksek kaliteli galvanizli çelik konstrüksiyon ile uzun ömürlü kullanım sağlar.
    
    Otomatik kapanma sistemi ve panik bar donanımı ile acil durumlarda hızlı ve güvenli tahliye 
    imkanı sunar. Duman sızdırmazlık özelliği sayesinde yangın anında duman geçişini önler.`,
    relatedProducts: [2, 3, 4],
  };

  const vm = React.useMemo(() => {
    const p: any = fetchedProduct || {};
    const title =
      (langIsTr ? p?.locales?.tr?.title : p?.locales?.en?.title) ||
      p?.title ||
      slug;
    const subtitle =
      (langIsTr ? p?.locales?.tr?.subtitle : p?.locales?.en?.subtitle) ||
      p?.subtitle ||
      "";
    const description =
      (langIsTr ? p?.locales?.tr?.description : p?.locales?.en?.description) ||
      p?.description ||
      "";
    const images: string[] =
      (Array.isArray(p?.images) && p.images.length ? p.images : null) ||
      (Array.isArray(p?.media?.images) && p.media.images.length
        ? p.media.images
        : []) ||
      [];
    const price =
      typeof p?.pricing?.price !== "undefined" ? p.pricing.price : p?.price;
    const showPrice =
      typeof p?.pricing?.showPrice === "boolean"
        ? p.pricing.showPrice
        : Boolean(p?.showPrice);
    const catalogPdf = p?.media?.catalogPdf || p?.catalogPdf || "";
    const model3d = p?.media?.model3d || p?.model3d || null;
    return {
      ...p,
      title,
      subtitle,
      description,
      images,
      price,
      showPrice,
      catalogPdf,
      model3d,
    };
  }, [fetchedProduct, langIsTr, slug]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
      {/* Breadcrumbs */}
      <Box sx={{ bgcolor: "white", borderBottom: "1px solid #eee" }}>
        <Container sx={{ py: 2 }}>
          <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              color="inherit"
            >
              {t("productDetail.breadcrumbs.home")}
            </Link>
            <Link
              component={RouterLink}
              to="/products"
              underline="hover"
              color="inherit"
            >
              {t("productDetail.breadcrumbs.products")}
            </Link>
            <Typography color="text.primary">{vm?.title || slug}</Typography>
          </Breadcrumbs>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/products")}
          sx={{ mb: 3 }}
        >
          {t("productDetail.backButton")}
        </Button>

        {/* Main Product Section */}
        <Grid container spacing={4}>
          {/* Image Gallery */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              {/* Main Image */}
              <Box
                sx={{
                  position: "relative",
                  mb: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#f5f5f5",
                  height: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {vm?.discount && (
                  <Badge
                    badgeContent={`-${vm.discount}%`}
                    color="error"
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: 16,
                      zIndex: 1,
                      "& .MuiBadge-badge": {
                        fontSize: "1rem",
                        height: 32,
                        minWidth: 32,
                        borderRadius: 16,
                      },
                    }}
                  />
                )}
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    bgcolor: "white",
                    zIndex: 1,
                  }}
                  onClick={() => setLiked(!liked)}
                >
                  {liked ? <Favorite color="error" /> : <FavoriteBorder />}
                </IconButton>
                <Box
                  component="img"
                  src={vm?.images?.[activeImage]}
                  alt={vm?.title || "product"}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
                <IconButton
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "white",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                  }}
                >
                  <ZoomIn />
                </IconButton>
              </Box>

              {/* Thumbnail Images */}
              <Grid container spacing={1}>
                {vm?.images?.map((img: string, idx: number) => (
                  <Grid key={idx} item xs={3}>
                    <Box
                      onClick={() => setActiveImage(idx)}
                      sx={{
                        border:
                          activeImage === idx ? "2px solid" : "1px solid #eee",
                        borderColor:
                          activeImage === idx ? "primary.main" : "#eee",
                        borderRadius: 1,
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "primary.main",
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={img}
                        alt={`${vm?.title || "product"} ${idx + 1}`}
                        sx={{
                          width: "100%",
                          height: 100,
                          objectFit: "contain",
                          bgcolor: "#f5f5f5",
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              {/* Header */}
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Chip
                  label={vm?.fireRating}
                  color="error"
                  icon={<LocalFireDepartment />}
                />
                <Chip label={vm?.category} variant="outlined" />
                {vm?.stock && (
                  <Chip
                    label={vm.stock}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>

              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {vm?.title || slug}
              </Typography>
              {vm?.subtitle && (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {vm.subtitle}
                </Typography>
              )}

              {/* Price or Contact */}
              <Box sx={{ mb: 3 }}>
                {vm?.showPrice && vm?.price ? (
                  <>
                    <Stack direction="row" spacing={2} alignItems="baseline">
                      <Typography
                        variant="h3"
                        color="primary"
                        sx={{ fontWeight: 800 }}
                      >
                        {i18n.language.startsWith("tr")
                          ? `₺${vm.price.toLocaleString("tr-TR")}`
                          : `${vm.price.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}`}
                      </Typography>
                      {vm?.oldPrice && (
                        <Typography
                          variant="h5"
                          color="text.secondary"
                          sx={{ textDecoration: "line-through" }}
                        >
                          {i18n.language.startsWith("tr")
                            ? `₺${vm.oldPrice.toLocaleString("tr-TR")}`
                            : `${vm.oldPrice.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}`}
                        </Typography>
                      )}
                    </Stack>
                    {vm?.sku && (
                      <Typography variant="body2" color="text.secondary">
                        {t("productDetail.vatIncluded")} • SKU: {vm.sku}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Chip label={t("productCard.contact")} />
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Features */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("productDetail.features")}
              </Typography>
              <List dense sx={{ mb: 3 }}>
                {(vm?.features || []).map((feature: any, idx: number) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36, color: "primary.main" }}>
                      <CheckCircle />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        typeof feature === "string" ? feature : feature.text
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {/* Color Options */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                {t("productDetail.colors")}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                {(vm.colors || []).map((color: any) => (
                  <Tooltip
                    key={color.name}
                    title={`${color.name} - ${color.label}`}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: color.hex,
                        border: "2px solid #ddd",
                        borderRadius: 1,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "scale(1.1)",
                          borderColor: "primary.main",
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Stack>

              {/* Actions */}
              <Stack spacing={2}>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  component={RouterLink}
                  to="/contact"
                  startIcon={<Description />}
                >
                  {t("productDetail.actions.quote")}
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ViewInAr />}
                    disabled={!vm?.model3d}
                  >
                    {t("productDetail.actions.view3d")}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Download />}
                    disabled={!vm?.catalogPdf}
                  >
                    {t("productDetail.actions.catalog")}
                  </Button>
                  <IconButton
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Share />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Trust Badges */}
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: "1px solid #eee",
                  justifyContent: "center",
                }}
              >
                <Chip
                  icon={<LocalShipping />}
                  label={t("productDetail.trust.freeInstall")}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  icon={<Support />}
                  label={t("productDetail.trust.warranty")}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  icon={<VerifiedUser />}
                  label={t("productDetail.trust.securePayment")}
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Detailed Information Tabs */}
        <Paper sx={{ mt: 4, borderRadius: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: "1px solid #eee",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
              },
            }}
          >
            <Tab label={t("productDetail.tabs.description")} />
            <Tab label={t("productDetail.tabs.specs")} />
            <Tab label={t("productDetail.tabs.certificates")} />
            <Tab label={t("productDetail.tabs.installation")} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                {t("productDetail.sections.description")}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}
              >
                {product.description}
              </Typography>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                {t("productDetail.sections.specs")}
              </Typography>
              <Grid container spacing={3}>
                {product?.specifications &&
                  Object.entries(product.specifications).map(
                    ([category, specs]: any) => (
                      <Grid key={category} item xs={12} md={6}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            textTransform: "capitalize",
                          }}
                        >
                          {t(
                            `productDetail.specsCategories.${category}` as any
                          )}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableBody>
                              {specs.map((spec: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      color: "text.secondary",
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                  >
                                    {spec.label}
                                  </TableCell>
                                  <TableCell
                                    sx={{ borderBottom: "1px solid #f0f0f0" }}
                                  >
                                    {spec.value}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )
                  )}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                {t("productDetail.sections.certificatesTitle")}
              </Typography>
              <Grid container spacing={3}>
                {[
                  {
                    title: "TSE 441",
                    desc: t("certificatesItems.1.description"),
                    icon: <Shield />,
                  },
                  {
                    title: "CE EN 16034",
                    desc: t("certificatesItems.2.description"),
                    icon: <VerifiedUser />,
                  },
                  {
                    title: "TS EN 1634-1",
                    desc: t("certificatesItems.5.description"),
                    icon: <Security />,
                  },
                ].map((cert, idx) => (
                  <Grid key={idx} item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            mb: 2,
                            width: 56,
                            height: 56,
                          }}
                        >
                          {cert.icon}
                        </Avatar>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {cert.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cert.desc}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                {t("productDetail.sections.installationTitle")}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {t("productDetail.install.title")}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("productDetail.install.mount")}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("productDetail.install.standards")}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("productDetail.install.postCheck")}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {t("productDetail.maintenance.title")}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("productDetail.maintenance.periodic")}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("productDetail.maintenance.lubrication")}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("productDetail.maintenance.seal")}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProductDetail;
